import { Component, computed, effect, ElementRef, input, output, signal, viewChild, viewChildren } from '@angular/core';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidSelectionHandle } from '../../components/rid-selection-handle/rid-selection-handle';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';

@Component({
  selector: 'rid-recognize-shortest-form',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidBottomButton,
    RidOverlayFeedback,
    RidMechanicTitle,
    RidSelectionHandle,
  ],
  templateUrl: './rid-recognize-shortest-form.html',
  host: {
    class: 'block',
  },
})
export class RidRecognizeShortestForm {
  assignments = input.required<[prefix: string | undefined, root: string, suffix: string | undefined][]>();

  index = signal(0);
  progress = signal(0);
  isChecking = signal(false);

  assignment = computed(() => this.assignments()[this.index()]);

  word = computed(() => {
    const a = this.assignment();
    return a.join('').split('');
  });

  correctStartIndex = computed(() => {
    const a = this.assignment();
    return (a[0] ?? '').length;
  });

  correctEndIndex = computed(() => {
    const a = this.assignment();
    return (a[0] ?? '').length + a[1].length;
  });

  startIndex = signal(0);
  endIndex = signal(0);

  container = viewChild.required<ElementRef<HTMLElement>>('container');
  letterEls = viewChildren<ElementRef<HTMLElement>>('letterEl');
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');

  boundaryPositions = signal<number[]>([]);
  private ro: ResizeObserver | undefined = undefined;

  textDragAnchor = signal<number | undefined>(undefined);
  draggingHandle = signal<'START' | 'END' | undefined>(undefined);

  startHandleLeft = computed(() => this.boundaryPositions()[this.startIndex()] ?? 0);
  endHandleLeft = computed(() => this.boundaryPositions()[this.endIndex()] ?? 0);

  isSubsetSelected = computed(() => {
    const start = this.startIndex();
    const end = this.endIndex();
    return end > start && (start > 0 || end < this.word().length);
  });

  finished = output<void>();

  constructor() {
    effect(() => {
      this.startIndex.set(0);
      this.endIndex.set(this.word().length);
    });

    effect(() => {
      const els = this.letterEls();
      if (els.length === 0) return;
      requestAnimationFrame(() => {
        this.updateBoundaryPositions();
        this.ro?.disconnect();
        this.ro = new ResizeObserver(() => this.updateBoundaryPositions());
        this.ro.observe(this.container().nativeElement);
      });
    });
  }

  async onSubmit() {
    if (this.isChecking()) return;

    this.isChecking.set(true);

    const isCorrect = this.startIndex() === this.correctStartIndex() && this.endIndex() === this.correctEndIndex();

    if (isCorrect) {
      this.progress.set(this.progress() + 1);
      await this.overlayFeedback().showCorrect();

      const isLast = this.index() === this.assignments().length - 1;
      if (isLast) {
        this.finished.emit();
      } else {
        this.index.set(this.index() + 1);
      }
    } else {
      await this.overlayFeedback().showIncorrect();
    }

    this.isChecking.set(false);
  }

  private updateBoundaryPositions() {
    const els = this.letterEls();
    if (els.length === 0) return;

    const positions: number[] = [];

    // Derive the gap from the distance between the first two letters
    const gap =
      els.length >= 2 ? els[1].nativeElement.offsetLeft - (els[0].nativeElement.offsetLeft + els[0].nativeElement.offsetWidth) : 0;

    for (let i = 0; i <= els.length; i++) {
      if (i === 0) {
        positions.push(els[0].nativeElement.offsetLeft - gap / 2);
      } else if (i === els.length) {
        const last = els[els.length - 1].nativeElement;
        positions.push(last.offsetLeft + last.offsetWidth + gap / 2);
      } else {
        const prevEl = els[i - 1].nativeElement;
        const currEl = els[i].nativeElement;
        const prevRight = prevEl.offsetLeft + prevEl.offsetWidth;
        positions.push((prevRight + currEl.offsetLeft) / 2);
      }
    }

    this.boundaryPositions.set(positions);
  }

  onTextPointerDown(event: PointerEvent) {
    if (this.isChecking()) return;

    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    const idx = this.getNearestBoundaryIndex(event.clientX);
    this.textDragAnchor.set(idx);
  }

  onTextPointerMove(event: PointerEvent) {
    const anchor = this.textDragAnchor();
    if (anchor === undefined) return;

    const idx = this.getNearestBoundaryIndex(event.clientX);

    if (idx !== anchor) {
      this.startIndex.set(Math.min(anchor, idx));
      this.endIndex.set(Math.max(anchor, idx));
    }
  }

  onTextPointerUp(event: PointerEvent) {
    const anchor = this.textDragAnchor();
    if (anchor === undefined) return;

    (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    this.textDragAnchor.set(undefined);

    const idx = this.getNearestBoundaryIndex(event.clientX);
    if (idx === anchor) {
      if (idx < this.startIndex()) {
        this.startIndex.set(idx);
      } else if (idx > this.endIndex()) {
        this.endIndex.set(idx);
      } else {
        const distToStart = Math.abs(idx - this.startIndex());
        const distToEnd = Math.abs(idx - this.endIndex());
        if (distToStart <= distToEnd) {
          this.startIndex.set(idx);
        } else {
          this.endIndex.set(idx);
        }
      }
    } else {
      this.startIndex.set(Math.min(anchor, idx));
      this.endIndex.set(Math.max(anchor, idx));
    }
  }

  onHandleDragStart(handle: 'START' | 'END') {
    this.draggingHandle.set(handle);
  }

  onHandleDragMove(event: PointerEvent, handle: 'START' | 'END') {
    const idx = this.getNearestBoundaryIndex(event.clientX);

    if (handle === 'START') {
      this.startIndex.set(Math.min(idx, this.endIndex()));
    } else {
      this.endIndex.set(Math.max(idx, this.startIndex()));
    }
  }

  onHandleDragEnd(handle: 'START' | 'END') {
    this.draggingHandle.set(undefined);
  }

  private getNearestBoundaryIndex(clientX: number): number {
    const container = this.container().nativeElement;
    const containerRect = container.getBoundingClientRect();
    // Account for CSS transforms (e.g. scale) by comparing visual vs layout size
    const scale = containerRect.width / (container.offsetWidth || 1);
    const relativeX = (clientX - containerRect.left) / scale;
    const positions = this.boundaryPositions();

    let minDist = Infinity;
    let nearestIdx = 0;

    for (let i = 0; i < positions.length; i++) {
      const dist = Math.abs(relativeX - positions[i]);
      if (dist < minDist) {
        minDist = dist;
        nearestIdx = i;
      }
    }

    return nearestIdx;
  }
}
